// frontend/src/pages/crm/fieldFormat.tsx
// Shared field-activity primitives — Phase 5 (2026-07-29).
//
// 🔒 EXTRACTED, NOT COPIED. FieldAgentsPage, FieldAgentDetailPage and the
// TA/DA rate card all render the same colour ladder, the same money and the
// same "not set" wording. Two copies of the ladder would drift, and the moment
// orange means something different on two screens the manager stops trusting
// either one. Every one of these came out of FieldAgentsPage.tsx unchanged.
//
// ⚠️ NEVER RENDERED. The frontend does not compile locally (~71k pre-existing
// errors; react does not resolve), so everything here is UNVERIFIED.
//
// Spec: docs/superpowers/specs/2026-07-29-field-activity-and-sessions-design.md

import type { CSSProperties, ReactNode } from 'react';
import { joinPersonName } from '@/utils/names';

export type AxisAttainment = {
  target: number | null;
  actual: number;
  pct: number | null;
  met: boolean | null;
};

export type Attainment = {
  leadsCaptured: AxisAttainment;
  meetingsHeld: AxisAttainment;
  coloursAdvanced: AxisAttainment;
  daysOut: AxisAttainment;
};

/** Same ladder as FieldActivityPage / FieldSessionsPage — keep the three in sync. */
export const DOT: Record<string, string> = {
  red: '#D5433B',
  yellow: '#E0A02E',
  orange: '#E07A16',
  green: '#3AA06B',
  promoted: '#3AA06B',
};

/** The rung labels, in ladder order — so no screen invents its own wording. */
export const LADDER: Array<{ key: keyof typeof DOT; label: string }> = [
  { key: 'red', label: 'No number' },
  { key: 'yellow', label: 'Site contact' },
  { key: 'orange', label: 'Referred on' },
  { key: 'green', label: 'DM found' },
  { key: 'promoted', label: 'Promoted' },
];

/** Local-calendar YYYY-MM-DD. 🔒 NOT toISOString() — that is UTC, and an IST
 *  evening would name the previous day. */
export function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Null means "never measured" and must render as an em dash, NOT as 0 km. */
export function km(metres: number | null | undefined): string {
  return metres == null ? '—' : `${(metres / 1000).toFixed(1)} km`;
}

/**
 * 🔒 Null renders as an em dash; ZERO renders as ₹0.00. "We do not know" and
 * "it is zero" are different facts, and with an empty rate card the second one
 * is the normal case.
 */
export function rupees(paise: number | null | undefined): string {
  if (paise == null) return '—';
  return `₹${(paise / 100).toFixed(2)}`;
}

export function elapsed(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function pingAge(iso: string | null | undefined): string {
  if (!iso) return 'no location yet';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 'no location yet';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  return mins < 1 ? 'just now' : `${elapsed(mins)} ago`;
}

export const fullName = (u: { firstName?: string; lastName?: string } | null | undefined): string =>
  `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim() || 'Unknown';

/**
 * Rupee string → integer paise, or null when the field is blank.
 *
 * 🔒 The ONLY place rupees become paise. A float never reaches the wire: the
 * API rejects a non-integer outright, and a rounding dispute over ₹0.005 is
 * exactly the kind of thing that destroys trust in a travel claim.
 * 🔒 Blank is null, NOT 0 — the caller decides what a blank field means, and
 * on the target form blank means "not measured" rather than "zero".
 */
export function toPaise(input: string): number | null {
  const s = (input ?? '').trim();
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** Integer paise → the rupee string that goes back into an <input>. */
export function paiseToInput(paise: number | null | undefined): string {
  if (paise == null) return '';
  return (paise / 100).toFixed(2);
}

/** Wall-clock HH:MM in the viewer's own timezone. Em dash for a missing time. */
export function hhmm(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || !iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** A running session (no endedAt) measures to NOW, which is the honest answer. */
export function duration(startedAt: string, endedAt: string | null): string {
  const a = new Date(startedAt).getTime();
  const b = endedAt ? new Date(endedAt).getTime() : Date.now();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return '—';
  return elapsed(Math.round((b - a) / 60000));
}

/**
 * What a meeting was WITH. Precedence MIRRORS the backend's
 * `describeAttachment` (deal > contact > field_lead): once a site has a real
 * Deal or Contact, that is the identity the rest of the CRM knows it by, and
 * the LeadImport link becomes provenance rather than the subject.
 *
 * 🔒 Never renders blank. Phase 2 read `m.subject` — a key ScheduledMeeting
 * does not have — so every row said the literal word "Meeting".
 *
 * ⚠️ Needs the RELATIONS to be present on the row. `GET /field-sessions`
 * includes them (MEETING_SELECT); `GET /field-agents/:userId` does NOT — it
 * selects contactId/dealId/leadImportId only — so on the manager drill-down
 * this degrades to the address. Reported, deliberately not fixed by widening
 * the API here.
 */
export function meetingSubject(m: any): string {
  const deal = (m?.deal?.title ?? '').trim();
  if (deal) return deal;
  const contact = joinPersonName(m?.contact?.firstName, m?.contact?.lastName);
  if (contact) return contact;
  const lead = (m?.leadImport?.fieldLeadTitle ?? '').trim();
  if (lead) return lead;
  const addr = (m?.addressLine1 ?? '').trim();
  return addr || 'Meeting';
}

/** 🔒 `source === 'field_session'` is a meeting the rep LOGGED after the fact;
 *  anything else was SCHEDULED. Never collapse the two — telling them apart is
 *  the entire reason the column exists. */
export const isLoggedMeeting = (m: any): boolean => m?.source === 'field_session';

export function Badge({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', marginRight: 6, padding: '2px 8px', borderRadius: 10,
      background: '#FDEDD6', color: '#B55A00', fontSize: 11, fontWeight: 700,
    }}>
      {children}
    </span>
  );
}

export const TH: CSSProperties = { padding: '6px 8px', fontWeight: 600 };
export const TD: CSSProperties = { padding: '8px' };

export const BTN_OK: CSSProperties = {
  padding: '4px 10px', marginRight: 6, borderRadius: 6, cursor: 'pointer',
  border: '1px solid #3AA06B', background: '#EAF6EF', color: '#2C6D4A', fontWeight: 600,
};
export const BTN_NO: CSSProperties = {
  padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid #D5433B', background: '#FDF0EF', color: '#A8322B', fontWeight: 600,
};

export function Tile({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ padding: '8px 12px', borderRadius: 10, background: `${color}1F`, minWidth: 96 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6b7280' }}>{label}</div>
    </div>
  );
}

/**
 * The five colour-ladder tiles, in ladder order, from a counts map.
 *
 * 🔒 The ONE place the ladder is rendered. The board and the drill-down both
 * call this, so they cannot drift on rung order, wording or colour.
 *
 * ⚠️ The `key` sits on a plain <div>, not on <Tile>. Without react's types
 * `key` is not recognised as a reserved prop and TypeScript checks it against
 * the component's props, producing a TS2322 that is pure noise. Intrinsic
 * elements are implicitly `any` here, so a wrapper div takes the key cleanly.
 * The wrapper is layout-neutral: the parent is a flex row and the div simply
 * becomes the flex item in the Tile's place.
 */
export function LadderTiles({ matrix }: { matrix: Record<string, number> | null | undefined }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
      {LADDER.map((rung) => (
        <div key={rung.key}>
          <Tile label={rung.label} value={matrix?.[rung.key as string] ?? 0} color={DOT[rung.key]} />
        </div>
      ))}
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 12, letterSpacing: 0.6, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p style={{ color: '#9ca3af', fontSize: 14 }}>{children}</p>;
}

/**
 * 🔒 An axis with no target reads "not set". It must NEVER be rendered as 0%,
 * which would say the rep failed a target nobody ever entered.
 */
export function AxisChip({ label, a }: { label: string; a: AxisAttainment | undefined }) {
  if (!a) return null;
  if (a.target == null) {
    return (
      <div style={{ minWidth: 120 }}>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
        <div style={{ fontSize: 13, color: '#9ca3af' }}>{a.actual} · not set</div>
      </div>
    );
  }
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: a.met ? '#3AA06B' : '#B55A00' }}>
        {a.actual} / {a.target} · {a.pct}%
      </div>
    </div>
  );
}
